"use client";

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { LuCheck, LuChevronDown } from "react-icons/lu";

/**
 * Select-only combobox.
 *
 * A native <select> hands its option list to the OS, which ignores the page's
 * dark theme on Windows and drops a plain white list on top of the section, so
 * the list is drawn here instead. Focus stays on the trigger and the highlighted
 * row is tracked with aria-activedescendant (the ARIA select-only combobox
 * pattern), which keeps keyboard and screen-reader behaviour close to native.
 */
export default function Dropdown({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: string;
    options: string[];
    onChange: (v: string) => void;
}) {
    const [open, setOpen] = useState(false);
    /** Row the keyboard is on; only meaningful while open. */
    const [active, setActive] = useState(0);
    const wrap = useRef<HTMLDivElement>(null);
    const uid = useId();
    const optionId = (i: number) => `${uid}-o${i}`;

    useEffect(() => {
        if (!open) return;
        const away = (e: PointerEvent) => {
            if (!wrap.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("pointerdown", away);
        return () => document.removeEventListener("pointerdown", away);
    }, [open]);

    const reveal = () => {
        setActive(Math.max(0, options.indexOf(value)));
        setOpen(true);
    };
    const pick = (i: number) => {
        onChange(options[i]);
        setOpen(false);
    };
    const move = (delta: number) =>
        setActive((a) => Math.min(options.length - 1, Math.max(0, a + delta)));

    const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        switch (e.key) {
            case "ArrowDown":
            case "ArrowUp":
                e.preventDefault();
                if (open) move(e.key === "ArrowDown" ? 1 : -1);
                else reveal();
                break;
            case "Enter":
            case " ":
                e.preventDefault();
                if (open) pick(active);
                else reveal();
                break;
            case "Home":
            case "End":
                if (open) {
                    e.preventDefault();
                    setActive(e.key === "Home" ? 0 : options.length - 1);
                }
                break;
            case "Escape":
                if (open) {
                    e.preventDefault();
                    setOpen(false);
                }
                break;
            case "Tab":
                setOpen(false);
                break;
        }
    };

    return (
        <div className="relative" ref={wrap}>
            <button
                type="button"
                role="combobox"
                aria-label={label}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={`${uid}-list`}
                aria-activedescendant={open ? optionId(active) : undefined}
                onClick={() => (open ? setOpen(false) : reveal())}
                onKeyDown={onKeyDown}
                className="flex w-full items-center justify-between gap-2 rounded-xl pl-3.5 pr-3 py-2.5 font-poppins text-xs text-gray-200 cursor-pointer transition-colors hover:border-purple-500/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/60"
                style={{ background: "#19131f", border: `1px solid ${open ? "#7c44e566" : "#7c44e522"}` }}
            >
                <span className="truncate">{value}</span>
                <LuChevronDown
                    size={13}
                    className={`shrink-0 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <ul
                    id={`${uid}-list`}
                    role="listbox"
                    aria-label={label}
                    className="absolute left-0 right-0 z-30 mt-2 max-h-64 overflow-auto rounded-xl py-1"
                    style={{
                        background: "rgba(25,19,31,0.98)",
                        border: "1px solid #7c44e533",
                        boxShadow: "0 18px 40px rgba(0,0,0,0.6)",
                        backdropFilter: "blur(8px)",
                    }}
                >
                    {options.map((o, i) => {
                        const selected = o === value;
                        return (
                            <li
                                key={o}
                                id={optionId(i)}
                                role="option"
                                aria-selected={selected}
                                onPointerEnter={() => setActive(i)}
                                onClick={() => pick(i)}
                                className={`flex cursor-pointer items-center justify-between gap-2 px-3.5 py-2 font-poppins text-xs transition-colors ${
                                    selected ? "text-purple-200" : "text-gray-300"
                                } ${i === active ? "bg-white/[0.07]" : ""}`}
                            >
                                <span className="truncate">{o}</span>
                                {selected && <LuCheck size={12} className="shrink-0 text-purple-300" />}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
